'use client';

import React, { useEffect, useRef, useState } from 'react';
import styles from './Notification_Toast_Primary.module.scss';
import { useNotificationsStoreActions } from '@/store';
import { v4 as uuidv4 } from 'uuid';

interface Notification_Toast_Primary_Props {
	id: string;
	children?: React.ReactNode;
}

const Notification_Toast_Primary = ({
	id,
	children,
}: Notification_Toast_Primary_Props) => {
	const { removeNotification } = useNotificationsStoreActions();
	const toastRef = useRef<HTMLDivElement>(null);
	const toastId = useRef(uuidv4());

	const animationIn = 0.2;
	const animationOut = 0.2;
	const delayOut = 3; // seconds
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const removeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [isPaused, setIsPaused] = useState(false);

	const animationStyle: React.CSSProperties = {
		animationDuration: `${animationIn}s, ${animationOut}s`,
		animationDelay: `0s, ${delayOut}s`,
		animationPlayState: isPaused ? 'paused' : 'running',
	};

	const progressBarStyle: React.CSSProperties = {
		animationDuration: `${delayOut}s`,
		animationPlayState: isPaused ? 'paused' : 'running',
	};

	useEffect(() => {
		if (!id) return;

		const startTimeouts = () => {
			timeoutRef.current = setTimeout(() => {
				if (toastRef.current) {
					const toast = toastRef.current;
					toast.style.height = `${toast.scrollHeight}px`;
					toast.getBoundingClientRect(); // force reflow
					toast.style.transition =
						'height 0.3s ease-in-out, margin 0.1s, padding 0.3s';

					requestAnimationFrame(() => {
						toast.style.height = '0px';
						toast.style.margin = '0px';
						toast.style.padding = '0px';
					});
				}
				removeTimeoutRef.current = setTimeout(() => {
					removeNotification(id);
				}, delayOut * 100);
			}, delayOut * 1000);
		};

		startTimeouts();

		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			if (removeTimeoutRef.current) clearTimeout(removeTimeoutRef.current);
		};
	}, [id, removeNotification]);

	const handleMouseEnter = () => {
		setIsPaused(true);
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		if (removeTimeoutRef.current) clearTimeout(removeTimeoutRef.current);
	};

	const handleMouseLeave = () => {
		setIsPaused(false);
		// Restart timeouts
		timeoutRef.current = setTimeout(() => {
			if (toastRef.current) {
				const toast = toastRef.current;
				toast.style.height = `${toast.scrollHeight}px`;
				toast.getBoundingClientRect();
				toast.style.transition =
					'height 0.3s ease-in-out, margin 0.1s, padding 0.3s';
				requestAnimationFrame(() => {
					toast.style.height = '0px';
					toast.style.margin = '0px';
					toast.style.padding = '0px';
				});
			}
			removeTimeoutRef.current = setTimeout(() => {
				removeNotification(id);
			}, 300);
		}, delayOut * 1000);
	};

	if (!id) return null;

	return (
		<div
			ref={toastRef}
			id={toastId.current}
			className="overflow-hidden duration-300 ease-in-out h-fit"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<div className="py-2">
				<div
					style={animationStyle}
					className={`${styles['notification-slide-in']} bg-powder-blue flex items-start gap-4 px-4 py-3 rounded-lg shadow-sm w-screen max-w-[320px] relative`}
				>
					<span
						style={progressBarStyle}
						className={`${styles['timer-bar']} absolute bottom-0 right-0 h-1 z-10 w-full  bg-[#00FFA3]`}
					/>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="30"
						height="31"
						viewBox="0 0 30 31"
						fill="none"
						className="min-w-[30px] min-h-[30px]"
					>
						<g clipPath="url(#clip0_23754_187396)">
							<path
								d="M14.9998 0.941406C12.1205 0.941406 9.3059 1.79521 6.91186 3.39485C4.51783 4.9945 2.65191 7.26813 1.55005 9.92824C0.448202 12.5883 0.159907 15.5155 0.721627 18.3394C1.28335 21.1634 2.66985 23.7573 4.70581 25.7933C6.74177 27.8292 9.33574 29.2158 12.1597 29.7775C14.9836 30.3392 17.9108 30.0509 20.5709 28.949C23.231 27.8472 25.5046 25.9813 27.1042 23.5872C28.7039 21.1932 29.5577 18.3786 29.5577 15.4993C29.5533 11.6397 28.0181 7.93935 25.2889 5.21016C22.5598 2.48098 18.8594 0.945794 14.9998 0.941406Z"
								fill="#00FFA3"
							/>
							<path
								d="M18.873 11.8491L12.9668 17.7553C12.5815 18.1407 12.5815 18.7655 12.9668 19.1509C13.3522 19.5363 13.9771 19.5363 14.3625 19.1509L20.2687 13.2447C20.654 12.8593 20.654 12.2345 20.2687 11.8491C19.8833 11.4637 19.2584 11.4637 18.873 11.8491Z"
								fill="#121416"
							/>
							<path
								d="M9.73174 15.9324L12.9511 19.1518C13.3365 19.5372 13.9613 19.5372 14.3467 19.1518C14.7321 18.7664 14.7321 18.1416 14.3467 17.7562L11.1273 14.5368C10.742 14.1514 10.1171 14.1514 9.73174 14.5368C9.34635 14.9222 9.34635 15.547 9.73174 15.9324Z"
								fill="#121416"
							/>
						</g>
						<defs>
							<clipPath id="clip0_23754_187396">
								<rect
									width="30"
									height="30"
									fill="white"
									transform="translate(0 0.5)"
								/>
							</clipPath>
						</defs>
					</svg>
					{children}
				</div>
			</div>
		</div>
	);
};

export default Notification_Toast_Primary;

// {
// 	/* <div className="flex flex-col">
// <p className="font-bold">Instant Rakeback Claimed!</p>
// <p className="text-xs">
// 	You successfully claimed your Emerald 3 Instant Rebate!
// </p>
// <p className="flex items-center justify-between mt-1 text-xs">
// 	Total Reward: <span>€40</span>
// </p>
// </div> */
// }
